import type { Feature } from './config'

/**
 * Best-effort mapping from URL pathname → feature tag. Called from the
 * client-side window.onerror handler so window-level crashes get a
 * feature (`booking`, `payment`, etc.) instead of the generic
 * `'unknown'` — the filter facet in the admin viewer is only useful if
 * most rows have one.
 *
 * Longest / most specific patterns first. Falls through to 'unknown'.
 */
const PATTERNS: Array<[RegExp, Feature]> = [
  // Booking flows — must match BEFORE the generic /therapist/ profile route
  [/\/(book|booking|confirm-booking)\b/i, 'booking'],
  [/\/therapist\/[^/]+\/book/i, 'booking'],
  [/\/api\/booking/i, 'booking'],

  // Payments + Stripe
  [/\/(payment|billing|stripe|invoice|checkout|payout)/i, 'payment'],

  // Auth surfaces
  [/\/(login|signup|signin|sign-in|sign-up|forgot-password|reset-password|mfa|verify)/i, 'auth'],
  [/\/api\/auth/i, 'auth'],

  // Supervision
  [/\/supervision/i, 'supervision'],

  // Scheduling
  [/\/(schedule|availability|calendar)/i, 'scheduling'],

  // Sessions viewing (not booking)
  [/\/sessions?(\b|\/)/i, 'sessions'],

  // Support / messaging
  [/\/(messages?|chat)/i, 'messaging'],
  [/\/(support|ticket|help)/i, 'support'],

  // Profile / settings
  [/\/(profile|settings|account)/i, 'profile'],
  [/\/therapist\/[^/]+$/i, 'profile'],

  // Onboarding / intake / consent
  [/\/(intake|questionnaire|onboarding|welcome)/i, 'intake'],
  [/\/consent/i, 'consent'],

  // Referrals
  [/\/referral/i, 'referrals'],

  // Clients (therapist-facing)
  [/\/clients?(\b|\/)/i, 'clients'],
  [/\/progress-notes|\/charts?\b/i, 'progress-notes'],
  [/\/assessments?/i, 'assessments'],

  // Admin
  [/\/admin/i, 'admin'],

  // Dashboard (catch-all for /dashboard/*)
  [/\/dashboard/i, 'dashboard'],
]

export function inferFeatureFromPath(pathname: string | null | undefined): Feature {
  if (!pathname) return 'unknown'
  for (const [pattern, feature] of PATTERNS) {
    if (pattern.test(pathname)) return feature
  }
  return 'unknown'
}
